import type { ContentSearchOption } from "./searchAtoms";

interface ContentfulItem {
	sys: { id: string };
	fields: Record<string, unknown>;
}

type RichTextNode = Record<string, unknown>;

const INTERNAL_NOTE_PATTERNS = [
	/DO NOT DELETE/i,
	/THIS ENTRY IS FOR/i,
	/OUR FRONTEND REFERENCES/i,
	/DISPLAYS THE .+ PAGE/i,
];

const isInternalNote = (text: string): boolean => {
	const letters = text.replace(/[^a-zA-Z]/g, "");
	if (letters.length > 0 && text.length > 30) {
		const upperRatio = text.replace(/[^A-Z]/g, "").length / letters.length;
		if (upperRatio > 0.6) return true;
	}
	return INTERNAL_NOTE_PATTERNS.some((p) => p.test(text));
};

const isRichTextDoc = (val: unknown): val is RichTextNode =>
	!!val &&
	typeof val === "object" &&
	(val as RichTextNode).nodeType === "document";

const isHeadingNode = (nodeType: unknown): boolean =>
	typeof nodeType === "string" && nodeType.startsWith("heading-");

const extractRichText = (node: unknown): string => {
	if (!node || typeof node !== "object") return "";
	const n = node as RichTextNode;
	if (n.nodeType === "text" && typeof n.value === "string") {
		return isInternalNote(n.value) ? "" : n.value;
	}
	if (Array.isArray(n.content)) {
		return n.content.map(extractRichText).join(" ");
	}
	return "";
};

const findFirstHeading = (node: unknown): string | null => {
	if (!node || typeof node !== "object") return null;
	const n = node as RichTextNode;
	if (isHeadingNode(n.nodeType)) {
		const text = extractRichText(n).trim();
		if (text && !isInternalNote(text)) return text;
	}
	if (Array.isArray(n.content)) {
		for (const child of n.content) {
			const heading = findFirstHeading(child);
			if (heading) return heading;
		}
	}
	return null;
};

const TITLE_KEYS = ["headline", "header", "title", "subtitle", "subheadline"];

const TEXT_FIELD_KEYS = new Set([
	...TITLE_KEYS,
	"body",
	"text",
	"label",
	"summary",
	"caption",
	"articleMainHeader",
	"question",
	"answer",
]);

const getModuleTitle = (fields: Record<string, unknown>): string | null => {
	for (const key of TITLE_KEYS) {
		if (typeof fields[key] === "string" && fields[key])
			return fields[key] as string;
	}
	for (const val of Object.values(fields)) {
		if (isRichTextDoc(val)) {
			const heading = findFirstHeading(val);
			if (heading) return heading;
		}
	}
	return null;
};

const extractTextFromFields = (fields: Record<string, unknown>): string[] => {
	const texts: string[] = [];
	for (const [key, val] of Object.entries(fields)) {
		if (typeof val === "string" && TEXT_FIELD_KEYS.has(key)) {
			texts.push(val);
		} else if (isRichTextDoc(val)) {
			const richText = extractRichText(val);
			if (richText.trim()) texts.push(richText);
		}
	}
	return texts;
};

const getPageTitle = (fields: Record<string, unknown>): string => {
	const pageMeta = fields.pageMeta as
		| { fields?: { title?: string } }
		| undefined;
	const fallback =
		(fields.articleMainHeader as string) ||
		pageMeta?.fields?.title ||
		(fields.name as string) ||
		(fields.title as string) ||
		"Unknown";

	const modules = fields.modules as ContentfulItem[] | undefined;
	if (!Array.isArray(modules)) return fallback;

	const firstTitle = modules.reduce<string | null>(
		(found, m) => found || getModuleTitle(m?.fields as Record<string, unknown>),
		null,
	);
	return firstTitle || fallback;
};

export const extractDvSearchOptions = (
	dvPage: ContentfulItem,
	termsToMatch: string[],
	extractSnippets: (
		fields: Record<string, unknown>,
		termsToMatch: string[],
	) => string[],
): ContentSearchOption[] => {
	const dvUrl =
		(dvPage.fields.relativeUrl as string)?.trim() || "/domestic-violence";
	const modules = (dvPage.fields.modules as ContentfulItem[]) || [];
	const fallbackTitle = getPageTitle(dvPage.fields);

	const results: ContentSearchOption[] = [];
	let lastKnownTitle = fallbackTitle;

	const processTexts = (texts: string[], title: string) => {
		if (texts.length === 0) return;
		const snippets = extractSnippets(
			{ _dvText: texts.join("\n") } as Record<string, unknown>,
			termsToMatch,
		);
		for (const snippet of snippets) {
			results.push({
				label: title,
				value: dvPage.sys.id,
				urls: [dvUrl],
				snippet,
			});
		}
	};

	for (const mod of modules) {
		if (!mod?.fields) continue;
		const fields = mod.fields as Record<string, unknown>;
		const title = getModuleTitle(fields);
		if (title && !isInternalNote(title)) lastKnownTitle = title;
		processTexts(extractTextFromFields(fields), lastKnownTitle);
	}

	processTexts(
		extractTextFromFields(dvPage.fields as Record<string, unknown>),
		fallbackTitle,
	);

	return results;
};
